import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  TextField,
  TileGroup,
  Tile,
  TileText,
  SkeletonLine,
  Alert
} from '@one-thd/sui-atomic-components';
import { FacetGroup as FacetGroupModel } from '../types';

export interface FacetGroupProps {
  group: FacetGroupModel;
  selectedValues: string[];
  onToggleOption: (value: string) => void;
  isLoading?: boolean;
}

const SKELETON_ROW_COUNT = 4;

const FacetGroupComponent: React.FC<FacetGroupProps> = ({ group, selectedValues, onToggleOption, isLoading = false }) => {
  const [search, setSearch] = useState('');
  const isSingleSelect = group.selectionMode === 'single';

  const filteredOptions = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) {
      return group.options;
    }
    return group.options.filter((option) => option.label.toLowerCase().includes(normalized));
  }, [search, group.options]);

  return (
    <div className="sui-border-b-1 sui-border-solid sui-border-strong sui-pt-[14px] sui-pb-[14px]">
      <Accordion noBorders defaultExpanded={isSingleSelect}>
        <AccordionHeader aria-controls={`${group.key}-facet-content`}>
          {isSingleSelect || selectedValues.length === 0 ? group.title : `${group.title} (${selectedValues.length})`}
        </AccordionHeader>
        <AccordionBody id={`${group.key}-facet-content`}>
          {!isSingleSelect && (
            <div className="sui-mb-[16px]">
              <TextField
                value={search}
                label={`Search ${group.title}`}
                onChange={(event: { target: { value: string } }) => setSearch(event.target.value)}
                fullWidth
              />
            </div>
          )}
          {isLoading ? (
            <div className="sui-flex sui-flex-col sui-gap-[12px]">
              {Array.from({ length: SKELETON_ROW_COUNT }, (_value, index) => index).map((index) => (
                <SkeletonLine key={`${group.key}-facet-skeleton-${index}`} />
              ))}
            </div>
          ) : isSingleSelect ? (
            <TileGroup
              orientation="vertical"
              value={selectedValues[0] ?? null}
              onChange={(_event: unknown, value: string | null) => value && onToggleOption(value)}
            >
              {group.options.map((option) => (
                <Tile key={option.value} value={option.value} aria-label={option.label}>
                  <TileText>{option.label}</TileText>
                </Tile>
              ))}
            </TileGroup>
          ) : (
            <>
              {filteredOptions.length === 0 && (
                <Alert status="error" headline="No matches found">
                  Try adjusting your search term.
                </Alert>
              )}
              <TileGroup orientation="vertical">
                {filteredOptions.map((option) => (
                  <Tile
                    key={option.value}
                    value={option.value}
                    aria-label={option.label}
                    selected={selectedValues.includes(option.value)}
                    onClick={() => onToggleOption(option.value)}
                  >
                    <TileText>{option.label}</TileText>
                  </Tile>
                ))}
              </TileGroup>
            </>
          )}
        </AccordionBody>
      </Accordion>
    </div>
  );
};

FacetGroupComponent.displayName = 'FacetGroup';

const FacetGroup = FacetGroupComponent;

export { FacetGroup };
